import { CompanyStore } from "../models/company.store.model";
import api from "../lib/api";
import { Pagination, TableApiRes } from "../models/base.model";
import { deleteConfirmDialog } from "./common.action";
import { useEffect, useMemo } from "react";
import { useState } from "react";

export async function getCompanyStores(param?: Pagination): Promise<TableApiRes<CompanyStore>> {
  const result = await api<{
    stores: CompanyStore[];
    offset?: any;
    total?: number;
  }>({
    url: `/company-stores/filter`,
    method: "POST",
    data: param,
  });
  const data = result.stores;
  return {
    data,
    total: result.total,
    offset: result.offset,
  };
}

export async function getCompanyStoresByCompanyId(id: string, param?: Pagination): Promise<TableApiRes<CompanyStore>> {
  const data = (
    await api<{ stores: CompanyStore[] }>({
      url: `/company-stores/company/${id}`,
      method: "post",
      data: param,
    })
  ).stores;
  return { data, total: data.length };
}

export function useCompanyStores(companyId: string | undefined) {
  const [companyStores, setCompanyStores] = useState<CompanyStore[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    getCompanyStoresByCompanyId(companyId)
      .then(({ data }) => setCompanyStores(data))
      .finally(() => setLoading(false));
  }, [companyId]);

  return useMemo(() => ({ companyStores, loading }), [companyStores, loading]);
}

export async function addCompanyStore(param: CompanyStore): Promise<CompanyStore> {
  const data = (
    await api<{ store: CompanyStore }>({
      url: `/company-stores`,
      data: param,
      method: "POST",
    })
  ).store;
  return data;
}

export async function updateCompanyStore(param: Partial<CompanyStore>) {
  const data = (
    await api<{ store: CompanyStore }>({
      url: `/company-stores/${param.id}`,
      data: param,
      method: "PATCH",
    })
  ).store;
  return data;
}

export async function deleteCompanyStore(id: string, companyId: string) {
  await deleteConfirmDialog(() =>
    api({
      url: `/company-stores/${id}`,
      method: "DELETE",
      data: {
        sk: companyId,
      },
    })
  );
}

export async function getCompanyStoreById(id: string) {
  const data = (
    await api<{ store: CompanyStore }>({
      url: `/company-stores/${id}`,
    })
  ).store;
  return data;
}

export async function batchCreateCompanyStores(params: { stores: CompanyStore[] }) {
  if (params.stores.length === 0) {
    throw new Error("No store found to import, please upload and try again!");
  }
  const data = (
    await api<{ stores: CompanyStore }>({
      url: `/company-stores/batch`,
      data: params,
      method: "POST",
      message: `Successfully batch import ${params.stores.length} company stores`,
    })
  ).stores;
  return data;
}
